if (!customElements.get('vazi-wall')) {
  customElements.define('vazi-wall', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.grid = this.querySelector('.vw-source');
      if (!this.grid) return;

      const template = this.grid.querySelector('template');
      const source = template ? template.content : this.grid;

      this.films = [...source.querySelectorAll('iframe')].flatMap(frame => {
        try {
          const url = new URL(frame.getAttribute('src'), location.href);
          const match = url.pathname.match(/^\/embed\/([\w-]{11})$/);

          if (
            !/(^|\.)youtube(-nocookie)?\.com$/.test(url.hostname) ||
            !match
          ) return [];

          return [{
            id: match[1],
            title: frame.title || 'Vazi film'
          }];
        } catch {
          return [];
        }
      });

      if (!this.films.length) return;

      this.grid.replaceChildren();
      this.grid.classList.add('vw-grid');
      this.active = new Map();
      this.dense = false;

      const count =
        Math.ceil(Math.max(this.films.length * 2, 32) / 4) * 4;

      this.cards = Array.from({ length: count }, (_, i) => {
        const film = this.films[i % this.films.length];
        const card = document.createElement('a');

        card.className = 'vw-tile';
        card.film = film;
        card.href = 'https://www.youtube.com/watch?v=' + film.id;
        card.target = '_blank';
        card.rel = 'noopener noreferrer';

        card.setAttribute(
          'aria-label',
          film.title + ' — opens on YouTube in a new tab'
        );

        card.style.background =
          '#fff url("https://i.ytimg.com/vi/' +
          film.id +
          '/hqdefault.jpg") center / cover no-repeat';

        this.grid.append(card);
        return card;
      });

      this.querySelector('.vw-actions').hidden = false;

      this.queue = () => {
        clearTimeout(this.pending);
        this.pending = setTimeout(() => this.update(), 140);
      };

      this.resize = () => {
        this.layout();
        this.queue();
      };

      this.visibility = () => this.update();

      this.key = event => {
        if (event.key === 'Escape') this.expand(false);
      };

      window.addEventListener('resize', this.resize, {
        passive: true
      });

      document.addEventListener('scroll', this.queue, {
        capture: true,
        passive: true
      });

      document.addEventListener('visibilitychange', this.visibility);
      document.addEventListener('keydown', this.key);

      this.querySelector('[data-action="density"]').onclick = event => {
        this.dense = !this.dense;
        event.currentTarget.textContent =
          this.dense ? 'Larger view' : 'Denser view';
        this.resize();
      };

      this.querySelector('[data-action="shuffle"]').onclick = () => {
        const shuffled = [...this.cards];

        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        shuffled.forEach((card, i) => {
          card.style.order = i;
        });

        this.queue();
      };

      this.querySelector('[data-action="expand"]').onclick = () => {
        this.expand(!this.classList.contains('vw-expanded'));
      };

      this.layout();
      this.update();
    }

    layout() {
      this.mobile = window.matchMedia(
        '(max-width: 899px), (pointer: coarse)'
      ).matches;

      const width = this.clientWidth;

      const columns =
        width < 600 ? 2 :
        width < 900 ? 3 :
        this.dense ? 5 : 4;

      this.grid.style.setProperty('--vw-cols', columns);
    }

    wanted() {
      if (document.hidden) return [];
      if (!this.mobile) return this.cards;

      const height = window.innerHeight;
      const width = window.innerWidth;

      return this.cards
        .map(card => {
          const rect = card.getBoundingClientRect();

          const visibleHeight = Math.max(
            0,
            Math.min(rect.bottom, height) - Math.max(rect.top, 0)
          );

          const visibleWidth = Math.max(
            0,
            Math.min(rect.right, width) - Math.max(rect.left, 0)
          );

          return {
            card,
            area: visibleHeight * visibleWidth
          };
        })
        .filter(item => item.area > 0)
        .sort((a, b) => b.area - a.area)
        .slice(0, 4)
        .map(item => item.card);
    }

    update() {
      if (!this.isConnected || !this.active) return;

      const wanted = new Set(this.wanted());

      this.active.forEach((frame, card) => {
        if (!wanted.has(card)) {
          frame.remove();
          this.active.delete(card);
        }
      });

      wanted.forEach(card => {
        if (this.active.has(card)) return;

        const frame = document.createElement('iframe');
        const id = card.film.id;

        frame.title = card.film.title;

        frame.src =
          'https://www.youtube-nocookie.com/embed/' + id +
          '?autoplay=1&mute=1&playsinline=1&rel=0&loop=1' +
          '&playlist=' + id +
          '&controls=0&disablekb=1';

        frame.allow =
          'autoplay; encrypted-media; picture-in-picture; fullscreen';

        frame.allowFullscreen = true;
        frame.tabIndex = -1;
        frame.style.pointerEvents = 'none';

        card.append(frame);
        this.active.set(card, frame);
      });
    }

    expand(on) {
      if (on === this.classList.contains('vw-expanded')) return;

      this.classList.toggle('vw-expanded', on);

      const button = this.querySelector('[data-action="expand"]');
      button.textContent = on ? 'Exit wall' : 'Expand wall';
      button.setAttribute('aria-pressed', String(on));

      if (on) {
        this.oldOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this.oldOverflow || '';
        button.focus();
      }

      this.resize();
    }

    disconnectedCallback() {
      clearTimeout(this.pending);

      window.removeEventListener('resize', this.resize);
      document.removeEventListener('scroll', this.queue, true);
      document.removeEventListener('visibilitychange', this.visibility);
      document.removeEventListener('keydown', this.key);

      if (this.active) {
        this.active.forEach(frame => frame.remove());
      }

      if (this.classList.contains('vw-expanded')) {
        document.body.style.overflow = this.oldOverflow || '';
      }

      this.ready = false;
    }
  });
}